import type { JSX } from "react";
import { Icon, Image, Link, ProgressBarSingle } from "@/components";
import type { CardCourseProps } from "@/types";

/**
 * @description Card component to display course information with light/dark mode support.
 * Demonstrates a card UI with stats, completion status, and hover effects.
 *
 * @component
 * @example
 * ```tsx
 * <Course
 *   title="UI/UX Design"
 *   modules={12}
 *   hours={4}
 *   progress={75}
 *   image="https://example.com/image.jpg"
 * />
 * ```
 *
 * @param {CardCourseProps} props - Component properties
 * @returns {JSX.Element} Rendered course card
 */
const Course = ({
  title,
  image,
  modules,
  hours,
  progress
}: CardCourseProps): JSX.Element => (
  <Link
    href="#"
    className="block box p-6 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151b2b] hover:border-slate-300 dark:hover:border-slate-600 transition-colors w-full"
  >
    <article className="bg-indigo-50/50 dark:bg-transparent rounded-2xl p-4 md:p-8 ">
      <header className="flex items-center gap-6 mb-6">
        <figure className="w-20 h-20 shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center border border-slate-200 dark:border-slate-700 shadow-sm relative overflow-hidden group">
          <div className="absolute inset-0 bg-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          {image ? (
            <Image
              src={image}
              alt={title}
              className="w-full h-full object-cover"
              width={80}
              height={80}
            />
          ) : (
            <Icon
              name="book-open"
              className="text-4xl text-blue-950 dark:text-blue-400"
            />
          )}
        </figure>

        <h4 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          {title}
        </h4>
      </header>

      <div className="flex items-center justify-between mb-6 text-base md:text-xl">
        <div className="flex items-center gap-2">
          <Icon name="video" className="text-slate-800 dark:text-white" />
          <span className="text-slate-600 dark:text-slate-300 font-medium px-2">
            {modules} Módulos
          </span>
        </div>

        <div className="flex items-center gap-2 text-base md:text-xl">
          <Icon
            name="clock-rotate-left"
            className="text-slate-800 dark:text-white"
          />
          <time
            className="text-base text-slate-600 dark:text-slate-300 font-medium"
            dateTime={`PT${hours}H`}
          >
            {hours} horas
          </time>
        </div>
      </div>

      <div className="border-t border-dashed border-slate-200 dark:border-slate-700 mb-4 pt-4"></div>

      <footer className="flex items-center gap-2 w-full">
        {progress === 100 ? (
          <>
            <Icon
              name="circle-check:fa:regular"
              className="text-indigo-500 text-lg"
            />
            <span className="text-slate-600 dark:text-slate-300 font-medium">
              Completed
            </span>
          </>
        ) : (
          <div className="flex items-center gap-3 w-full">
            <div className="flex-1 text-blue-950 dark:text-white">
              <ProgressBarSingle width={progress} height="h-2" />
            </div>
            <span className="text-sm text-zinc-900 dark:text-white">
              {progress}%
            </span>
          </div>
        )}
      </footer>
    </article>
  </Link>
);

export default Course;
